import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  Backdrop,
  FormControl,
  Select,
  MenuItem,
  Grid,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import * as Yup from "yup";

import { ImageUploader } from "../ImageUploader";
import { $crud } from "../../utils/CrudFactory";
import { BASE_URL } from "../../constants";
import { enqueueSnackbar } from "notistack";
import TipTapEditor from "../textEditor/TipTapEditor";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
    backdropFilter: "blur(2px)",
  },
  dialogPaper: {
    borderRadius: "0",
    border: "1px solid #000",
  },
  textFieldRoot: {
    "& .MuiInputLabel-root": {
      color: "#8D8D8E !important",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "rgba(103, 60, 142, 1)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(103, 60, 142, 1)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(103, 60, 142, 1)",
      },
    },
  },
}));

const MoreWaysModal = ({ onClose, open, onSubmit, view, row, id }) => {
  const classes = useStyles();

  // State to manage file upload
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState("");

  // Function to handle file changes
  const handleFileChange = (file) => {
    // Check if the file size exceeds 1 MB (1 MB = 1048576 bytes)
    if (file && file.size > 1048576) {
      enqueueSnackbar("Image size should not exceed 1 MB.", {
        variant: "error",
      });

      // Clear selected image data
      setUploadedFile(null);
      if (id != null) {
        setImageSrc(`${BASE_URL}moreWays/${row.image}`);
      } else {
        setImageSrc("");
      }
      setFieldValue("image", null); // Clear Formik field if the file is too large
      return;
    }

    // Set the selected file and read it as a data URL for preview
    setUploadedFile(file);
    const fileReader = new FileReader();
    fileReader.onload = (e) => setImageSrc(e.target.result);
    setFieldValue("image", file); // Update Formik field with the file object
    fileReader.readAsDataURL(file);
  };

  const initialValues = {
    id: id || "",
    title: "",
    case: "",
    tag: "",
    pickup: "",
    image: "",
  };

  useEffect(() => {
    if (id != null) {
      setValues({
        ...row,
        id,
      });
      setImageSrc(row.image);
    }
  }, [id]);

  //   const validationSchema = Yup.object({
  //     title: Yup.string().required("Required!"),
  //     subTitle: Yup.string().required("Required!"),
  //     description: Yup.string().required("Required!"),
  //   });

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues,
    onSubmit,
    // validationSchema,
  });

  const handleOfferSubmit = async () => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("case", values.case);
    formData.append("tag", values.tag);
    formData.append("pickup", values.pickup);
    if (id) {
      formData.append("id", id);
    }
    if (uploadedFile) {
      formData.append("file", values.image);
    }

    try {
      const { data } = id
        ? await $crud.put(`admin/more-ways`, formData)
        : await $crud.post(`admin/more-ways`, formData);

      if (data) {
        onClose();
        // Navigate after success
        // navigate("/services/regular-offers");
      }
    } catch (err) {
      // No need for error handling here, as it’s already handled by $crud.send
      console.log("more ways submission failed: ", err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Backdrop className={classes.backdrop} open={open} />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogContent sx={{ marginTop: "30px", padding: "0 40px" }}>
          <Typography
            id="modal-title"
            sx={{
              fontSize: "20px",
              mb: 2,
              fontWeight: 700,
              textAlign: "center",
            }}
            gutterBottom
          >
            {view ? "View " : id ? "Update " : "Add "}Client Use Cases to Use
            the App
          </Typography>

          <Grid container spacing={2}>
            {/* Title and Subtitle Fields */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "18px", marginBottom: "2px" }}>
                Event Name<span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                name="title"
                placeholder="PLAN A SURPRISE BIRTHDAY"
                sx={{
                  color: "#1C1B1F",
                  mb: 1,
                  "& .MuiInputLabel-root": {
                    color: "#1C1B1F !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: (theme) => theme.palette.text.secondary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1C1B1F",
                    },
                  },
                }}
                disabled={view}
                size="small"
                fullWidth
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                error={touched.title && !!errors.title}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: "18px", marginBottom: "2px" }}>
                Case<span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                name="case"
                placeholder={values.tag || "Case"}
                sx={{
                  color: "#1C1B1F",
                  mb: 1,
                  "& .MuiInputLabel-root": {
                    color: "#1C1B1F !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: (theme) => theme.palette.text.secondary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1C1B1F",
                    },
                  },
                }}
                disabled={view}
                size="small"
                fullWidth
                value={values.case}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                error={touched.case && !!errors.case}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: "18px", marginBottom: "2px" }}>
                Tags<span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                name="tag"
                placeholder="Events"
                sx={{
                  color: "#1C1B1F",
                  mb: 1,
                  "& .MuiInputLabel-root": {
                    color: "#1C1B1F !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: (theme) => theme.palette.text.secondary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1C1B1F",
                    },
                  },
                }}
                disabled={view}
                size="small"
                fullWidth
                value={values.tag}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                error={touched.tag && !!errors.tag}
              />
            </Grid>

            {/* CKEditor for Description */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "18px", marginBottom: "2px" }}>
                Steps<span style={{ color: "red" }}>*</span> (bullet points)
              </Typography>

              <TipTapEditor
                value={values.pickup || row?.pickup}
                onChange={(content) => setFieldValue("pickup", content)}
                disabled={view}
              />
            </Grid>

            {/* File Upload Section */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: "18px", marginBottom: "2px" }}>
                Upload Image<span style={{ color: "red" }}>*</span> (1050 x 495)
                px
              </Typography>
              <ImageUploader
                name="image"
                onChange={handleFileChange}
                id="image-upload"
                file={uploadedFile}
                src={imageSrc}
                required
                accept="image/*"
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              marginTop: "47px",
              marginBottom: "38px",
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            <Button
              sx={{
                borderColor: "#683C8C",
                color: "#683C8C",
                padding: "6px 40px",
                fontSize: "15px",
                fontWeight: 400,
                textTransform: "capitalize",
                "&:hover": {
                  borderColor: "#683C8C",
                  backgroundColor: "transparent",
                  color: "#683C8C",
                },
                mr: 1,
              }}
              variant="outlined"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              disabled={view} // Disable button if view is true
              sx={{
                backgroundColor: "#683C8C",
                color: "#FFFFFF",
                padding: "6px 40px",
                fontSize: "15px",
                fontWeight: 400,
                textTransform: "capitalize",
                "&:hover": {
                  backgroundColor: "#683C8C",
                  color: "#FFFFFF",
                },
              }}
              variant="contained"
              onClick={handleOfferSubmit}
            >
              {id ? "Update" : "Submit"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default MoreWaysModal;
